import React, { useState } from 'react'
import { Pencil, SmilePlus } from 'lucide-react'
import { cn } from 'lib/utils'

/** The tapback set iOS offers, in its order. */
export const TAPBACKS = ['❤️', '👍', '👎', '😂', '‼️', '❓']

export interface Props {
	messageId: string
	/** Which side the tapback row opens towards. */
	align?: 'left' | 'right'
	/** The current user's existing reaction, so it can be shown as selected. */
	myReaction?: string
	/** Offer the edit pencil. Only ever true for the user's latest message. */
	canEdit?: boolean
	/** Shown on hover; kept mounted so the popover can stay open. */
	isVisible?: boolean
	onReact?: (messageId: string, emoji: string) => void
	onStartEdit?: () => void
	className?: string
}

/**
 * The hover controls on a message: react, and edit when it is the user's most
 * recent. Shared by the global chat's bubbles and the room chats' rows, so the
 * tapback set and the one-reaction-per-person behaviour are defined once.
 */
const MessageActions = ({
	messageId,
	align = 'left',
	myReaction,
	canEdit = false,
	isVisible = false,
	onReact,
	onStartEdit,
	className = '',
}: Props) => {
	const [pickerOpen, setPickerOpen] = useState(false)

	if (!onReact && !canEdit) return null

	return (
		<span
			className={cn(
				'relative flex shrink-0 items-center gap-0.5 transition-opacity',
				isVisible || pickerOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
				className,
			)}
		>
			{onReact && (
				<button
					type="button"
					aria-label="React to message"
					onClick={() => setPickerOpen(v => !v)}
					className="grid h-6 w-6 place-items-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
				>
					<SmilePlus className="h-3.5 w-3.5" />
				</button>
			)}

			{canEdit && onStartEdit && (
				<button
					type="button"
					aria-label="Edit message"
					onClick={onStartEdit}
					className="grid h-6 w-6 place-items-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
				>
					<Pencil className="h-3.5 w-3.5" />
				</button>
			)}

			{pickerOpen && (
				<span
					role="group"
					aria-label="Reactions"
					className={cn(
						'absolute bottom-full z-20 mb-1 flex items-center gap-0.5 rounded-full border border-gray-200 bg-white px-1.5 py-1 shadow-lg',
						align === 'right' ? 'right-0' : 'left-0',
					)}
				>
					{TAPBACKS.map(emoji => (
						<button
							key={emoji}
							type="button"
							aria-label={`React with ${emoji}`}
							aria-pressed={myReaction === emoji}
							onClick={() => {
								onReact?.(messageId, emoji)
								setPickerOpen(false)
							}}
							className={cn(
								'grid h-7 w-7 place-items-center rounded-full text-[15px] transition-transform hover:scale-125',
								myReaction === emoji && 'bg-[#0080fe]/10',
							)}
						>
							{emoji}
						</button>
					))}
				</span>
			)}
		</span>
	)
}

export default MessageActions
